import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { PropertyDetails } from '../../../../core/models/property-details.model';
import { MakeOffer } from './make-offer/make-offer';
import { ScheduleViewing } from './schedule-viewing/schedule-viewing';
import { AccountProfileCardComponent } from '../../accounts-information/components/account-profile-card';
import { AccountProfile, AccountStat } from '../../accounts-information/components/accounts-information.models';
 
type SelectedPlanSummary = PropertyDetails['selected_plan_summary'];

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MakeOffer, ScheduleViewing, AccountProfileCardComponent],
  template: `
    <div class="sticky top-24 space-y-5 antialiased select-none">
      <div
        class="bg-surface-elevated rounded-3xl p-6 text-text-primary shadow-primary-glow relative overflow-hidden border border-primary/20 group"
      >
        <div class="absolute -right-16 -top-16 w-36 h-36 bg-primary/20 blur-3xl rounded-full pointer-events-none group-hover:bg-primary/30 transition-colors duration-500"></div>
        <div class="absolute -left-16 -bottom-16 w-36 h-36 bg-primary-deep/30 blur-3xl rounded-full pointer-events-none group-hover:bg-primary-deep/40 transition-colors duration-500"></div>

        <div class="flex items-center justify-between mb-4 relative z-10">
          <span class="text-text-muted text-[10px] font-black uppercase tracking-widest font-mono"
            >جدول خطة الدفع</span
          >
          <div
            class="px-2.5 py-1 bg-primary/10 border border-primary/30 rounded-lg text-[10px] font-bold tracking-wide text-primary font-mono uppercase"
          >
            {{
              valueOrDefault(
                selectedPlanSummary?.name || propertyData?.sidebar?.payment_plan_label,
                'أساسي'
              )
            }}
          </div>
        </div>

        <h2
          class="text-2xl sm:text-3xl font-black font-mono tracking-tight text-text-primary mb-4 relative z-10 drop-shadow-sm"
        >
          {{ valueOrDefault(propertyData?.sidebar?.price_display) }}
        </h2>

        <div
          class="space-y-2.5 pb-4 border-b border-border-subtle mb-5 font-medium text-xs relative z-10"
        >
          <div class="flex justify-between items-center py-0.5">
            <span class="text-text-muted">متوسط سعر المتر</span>
            <span class="font-bold font-mono text-text-primary">{{
              valueOrDefault(propertyData?.sidebar?.per_sqm)
            }}</span>
          </div>
          <div class="flex justify-between items-center py-0.5">
            <span class="text-text-muted">مبلغ المقدم</span>
            <span class="font-bold font-mono text-text-primary">
              {{
                valueOrDefault(
                  selectedPlanSummary?.down_payment || propertyData?.sidebar?.down_payment
                )
              }}
            </span>
          </div>
          <div class="flex justify-between items-center py-0.5">
            <span class="text-text-muted">القسط الشهري</span>
            <span class="font-bold font-mono text-text-primary">
              {{
                valueOrDefault(
                  selectedPlanSummary?.monthly_installment || propertyData?.sidebar?.monthly
                )
              }}
            </span>
          </div>
          <div class="flex justify-between items-center py-0.5">
            <span class="text-text-muted">مدة الخطة</span>
            <span class="font-bold font-mono text-text-primary">
              {{ valueOrDefault(selectedPlanSummary?.duration || propertyData?.sidebar?.duration) }}
            </span>
          </div>
        </div>

        <div class="space-y-2.5 relative z-10">
          <button
            class="w-full px-4 py-3 bg-bg-secondary hover:bg-surface-hover border border-border hover:border-primary/50 text-text-secondary hover:text-primary rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-wider font-mono cursor-pointer active:scale-[0.99]"
            type="button"
            (click)="onChangePlan()"
          >
            <i class="fa-solid fa-calculator-combined text-[11px] text-primary"></i>
            <span>تعديل الخطة</span>
          </button>

          @if (negotiable) {
            <button
              class="w-full px-4 py-3.5 btn btn-primary rounded-xl hover:-translate-y-0.5 transition-all duration-300 font-black text-xs uppercase tracking-widest font-mono flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99]"
              type="button"
              (click)="openMakeOfferModal()"
            >
              <i class="fa-solid fa-handshake-slant text-[11px]"></i>
              <span>تقديم عرض</span>
            </button>
          }

          <button
            class="w-full px-4 py-3.5 bg-surface text-text-primary border border-border-subtle rounded-xl hover:bg-surface-hover hover:border-primary/30 shadow-sm transition-all duration-300 font-black text-xs uppercase tracking-widest font-mono flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99]"
            type="button"
            (click)="openScheduleViewingModal()"
          >
            <i class="fa-solid fa-calendar-circle-user text-[11px] text-primary/80"></i>
            <span>تحديد موعد معاينة</span>
          </button>
        </div>
      </div>

      @if (profileData && !isProfileLoading) {
        <div class="mb-5">
          <app-account-profile-card 
            [profile]="profileData" 
            [stats]="statsData" 
            [clickableHeader]="true"
            (headerClick)="openAgentProfile()"
            class="block w-full scale-[0.98] origin-top-center mb-2"
          ></app-account-profile-card>
          <div class="surface-card rounded-3xl p-4 border border-border shadow-sm space-y-2">
            <a
              [href]="getPhoneLink()"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-bg-secondary hover:bg-success/10 text-text-secondary hover:text-success border border-border hover:border-success/30 rounded-xl transition-all font-mono text-xs font-black uppercase tracking-wider text-center"
              [class.pointer-events-none]="!ownerContact?.phoneNumber"
              [class.opacity-40]="!ownerContact?.phoneNumber"
            >
              <i class="fa-solid fa-phone-volume text-[11px]"></i>
              <span>{{ valueOrDefault(ownerContact?.phoneNumber, 'الهاتف غير متاح') }}</span>
            </a>
            <a
              [href]="getEmailLink()"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-bg-secondary hover:bg-info/10 text-text-secondary hover:text-info border border-border hover:border-info/30 rounded-xl transition-all font-mono text-xs font-black uppercase tracking-wider text-center"
              [class.pointer-events-none]="!ownerContact?.email"
              [class.opacity-40]="!ownerContact?.email"
            >
              <i class="fa-solid fa-envelope-open text-[11px]"></i>
              <span>{{ valueOrDefault(ownerContact?.email, 'البريد الإلكتروني غير متاح') }}</span>
            </a>
          </div>
        </div>
      } @else if (isProfileLoading) {
        <div class="animate-pulse bg-surface-elevated rounded-3xl h-64 border border-border shadow-sm mb-5 flex flex-col items-center justify-center text-text-muted gap-3">
          <i class="fa-solid fa-spinner fa-spin text-3xl"></i>
          <span class="text-xs font-mono font-bold">جاري تحميل بيانات الوكيل...</span>
        </div>
      }

      <div
        class="surface-card rounded-3xl p-5 border border-border shadow-sm overflow-hidden relative"
      >
        <div class="flex items-center gap-2 mb-4 pb-3 border-b border-dashed border-border-subtle">
          <div
            class="w-6 h-6 rounded-lg bg-info/10 flex items-center justify-center text-info shadow-sm border border-info/20"
          >
            <i class="fa-solid fa-wand-magic-sparkles text-[10px]"></i>
          </div>
          <div>
            <h4 class="text-[9px] font-black uppercase tracking-widest text-text-muted font-mono">
              تحليل ذكي
            </h4>
            <h3 class="text-xs font-black text-text-primary tracking-tight mt-0.5">
              رؤى محرك الذكاء الاصطناعي
            </h3>
          </div>
        </div>

        @if (insights.length) {
          <div class="space-y-3.5">
            @for (insight of insights; track insight.title + insight.detail) {
              <div class="flex items-start gap-3 group">
                <div
                  class="w-4 h-4 rounded bg-success/10 border border-success/20 flex items-center justify-center text-success shrink-0 mt-0.5"
                >
                  <i class="fa-solid fa-square-check text-[9px]"></i>
                </div>
                <div class="min-w-0">
                  <h5
                    class="text-xs font-bold text-text-primary tracking-tight group-hover:text-primary transition-colors"
                  >
                    {{ valueOrDefault(insight.title, 'تفاصيل الرؤية') }}
                  </h5>
                  <p class="text-[11px] font-medium text-text-secondary leading-relaxed mt-0.5">
                    {{ valueOrDefault(insight.detail) }}
                  </p>
                </div>
              </div>
            }
          </div>
        } @else {
          <div
            class="py-6 flex flex-col items-center justify-center border border-dashed border-border bg-bg-secondary/40 rounded-2xl text-center font-mono px-3"
          >
            <div
              class="w-8 h-8 rounded-lg bg-surface flex items-center justify-center text-text-muted mb-2 border border-border-subtle shadow-sm"
            >
              <i class="fa-solid fa-brain-circuit text-xs"></i>
            </div>
            <h4 class="text-[9px] font-black text-text-secondary uppercase tracking-wider mb-0.5">
              في انتظار التحليل
            </h4>
            <p
              class="text-[10px] font-medium text-text-muted max-w-[180px] leading-relaxed normal-case font-sans"
            >
              ستظهر الرؤى الذكية هنا بمجرد انتهاء تحليل البيانات.
            </p>
          </div>
        }
      </div>

      <button
        class="w-full flex items-center justify-center gap-2 py-2 text-text-muted hover:text-danger transition-colors text-[11px] font-black uppercase tracking-wider font-mono cursor-pointer bg-transparent border-none active:scale-[0.98]"
      >
        <i class="fa-solid fa-shield-exclamation text-xs"></i>
        <span>الإبلاغ عن هذا الإعلان</span>
      </button>
    </div>

    @if (showMakeOfferModal) {
      <div
        class="fixed inset-0 bg-bg-base/80 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fadeIn"
        (click)="closeMakeOfferModal()"
      >
        <div class="max-h-[90vh] overflow-y-auto" (click)="$event.stopPropagation()">
          <app-make-offer
            [targetType]="targetType"
            [targetId]="targetId || propertyData?.id || ''"
            [salePriceId]="salePriceId"
            (closeModal)="closeMakeOfferModal()"
          ></app-make-offer>
        </div>
      </div>
    }

    @if (showScheduleViewingModal) {
      <div
        class="fixed inset-0 bg-bg-base/80 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fadeIn"
        (click)="closeScheduleViewingModal()"
      >
        <div class="max-h-[90vh] overflow-y-auto" (click)="$event.stopPropagation()">
          <app-schedule-viewing
            [targetType]="targetType"
            [targetId]="targetId || propertyData?.id || ''"
            (closeModal)="closeScheduleViewingModal()"
          ></app-schedule-viewing>
        </div>
      </div>
    }
  `,
})
export class SidebarComponent implements OnChanges {
  @Input() propertyData: any;
  @Input() targetType: 'unit' | 'property' | 'project' = 'unit';
  @Input() targetId: string = '';
  @Input() salePriceId: string = '';
  @Input() agentInitial: string = '';
  @Input() ownerContact: { phoneNumber?: string; email?: string } | null = null;
  @Input() selectedPlanSummary: SelectedPlanSummary | null = null;
  @Input() negotiable: boolean = true;
  @Output() changePlanRequested = new EventEmitter<void>();

  showMakeOfferModal = false;
  showScheduleViewingModal = false;

  profileData: AccountProfile | null = null;
  statsData: AccountStat[] = [];
  isProfileLoading = false;

  constructor(private router: Router, private http: HttpClient) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyData'] && this.propertyData) {
      this.fetchOwnerProfile();
    }
  }

  fetchOwnerProfile(): void {
    const id = this.propertyData?.ownerId || '69ce980b5fe2679a5ad8c040';
    const roleType = this.propertyData?.ownerType || 'owner';
    const normalizedRole = roleType?.toLowerCase?.() || '';
    
    let endpoint = 'owner';
    if (normalizedRole === 'seeker' || normalizedRole === 'property_seeker') endpoint = 'seeker';
    if (normalizedRole === 'developer' || normalizedRole === 'real_estate_developer') endpoint = 'developer';

    this.isProfileLoading = true;
    this.http.get<any>(`${environment.api.baseUrl}/${endpoint}/${id}`).subscribe({
      next: (res) => {
        const resProfile =
          res?.resProfile ||
          res?.developerProfile ||
          res?.ownerProfile ||
          res?.seekerProfile ||
          res?.profile ||
          res ||
          {};

        const firstName = (resProfile.firstName || '').trim();
        const lastName = (resProfile.lastName || '').trim();
        const fullName = `${firstName} ${lastName}`.trim();

        let avatarUrl = resProfile.profileImage || resProfile.avatar || resProfile.photo || 'assets/default-avatar.png';
        if (avatarUrl && avatarUrl.startsWith('/')) {
          avatarUrl = `${environment.api.baseUrl}${avatarUrl}`;
        }

        this.profileData = {
          name: fullName || resProfile.name || resProfile.companyName || 'accountsInformation.profile.defaultName',
          role: resProfile.role || roleType,
          location: 'accountsInformation.profile.defaultLocation',
          memberSince: resProfile.createdAt ? new Date(resProfile.createdAt).getFullYear().toString() : 'accountsInformation.profile.defaultMemberSince',
          avatarUrl: avatarUrl,
          bio: resProfile.aboutMe || resProfile.bio || 'accountsInformation.profile.defaultBio',
          rating: Number(resProfile.averageRating) || 0,
          reviewsCount: Number(resProfile.totalReviews) || 0,
        };

        const successRate = Number(resProfile.successRate) || -1;
        const successRateText = successRate >= 0 ? `${successRate}%` : 'N/A';

        this.statsData = [
          {
            label: 'accountsInformation.stats.properties',
            value: (resProfile.propertiesCount || resProfile.numberOfPropertiesOwned || 0).toString(),
            containerClass: 'bg-primary-soft border-border-primary',
          },
          {
            label: 'accountsInformation.stats.successfulDeals',
            value: (resProfile.successfulDeals || 0).toString(),
            containerClass: 'bg-surface-elevated border-border',
          },
          {
            label: 'accountsInformation.stats.successRate',
            value: successRateText,
            containerClass: 'bg-success-soft border-success/20',
          },
          {
            label: 'accountsInformation.stats.rating',
            value: `${(Number(resProfile.averageRating) || 0).toFixed(1)} (${resProfile.totalReviews || 0})`,
            containerClass: 'bg-surface-elevated border-border',
          },
        ];

        this.isProfileLoading = false;
      },
      error: (err) => {
        console.error('Error fetching owner profile in sidebar:', err);
        this.isProfileLoading = false;
      }
    });
  }

  openAgentProfile(): void {
    const id = this.propertyData?.ownerId || '69ce980b5fe2679a5ad8c040';
    const roleType = this.propertyData?.ownerType || 'broker';
    
    const normalizedRole = roleType?.toLowerCase?.() || '';
    const roleSegment =
      normalizedRole === 'property_seeker' || normalizedRole === 'seeker'
        ? 'seeker'
        : normalizedRole === 'real_estate_developer' || normalizedRole === 'developer'
          ? 'developer'
          : normalizedRole === 'real_estate_broker' || normalizedRole === 'broker'
            ? 'broker'
            : 'owner';
            
    this.router.navigate(['/accounts-information', roleSegment, id]);
  }

  get insights(): Array<{ title: string; detail: string }> {
    return this.propertyData?.ai_insights || [];
  }

  valueOrDefault(value: unknown, fallback: string = 'غير متوفر'): string {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text ? text : fallback;
  }

  onChangePlan(): void {
    this.changePlanRequested.emit();
  }

  openMakeOfferModal(): void {
    this.showMakeOfferModal = true;
  }

  closeMakeOfferModal(): void {
    this.showMakeOfferModal = false;
  }

  openScheduleViewingModal(): void {
    this.showScheduleViewingModal = true;
  }

  closeScheduleViewingModal(): void {
    this.showScheduleViewingModal = false;
  }

  getPhoneLink(): string {
    const phoneNumber = this.ownerContact?.phoneNumber;
    if (!phoneNumber) return '#';

    // Remove any non-digit characters for the tel link
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    return `tel:${cleanNumber}`;
  }

  getEmailLink(): string {
    const email = this.ownerContact?.email;
    if (!email) return '#';

    const subject = encodeURIComponent(`استفسار حول ${this.propertyData?.title || 'العقار'}`);
    const body = encodeURIComponent(
      `مرحبًا،\n\nأنا مهتم بمعرفة المزيد عن هذا العقار. هل يمكنك تقديم معلومات إضافية؟\n\nشكرًا لك!`,
    );

    return `mailto:${email}?subject=${subject}&body=${body}`;
  }
}
